import { QAPair } from '../types';

/**
 * Filter configuration for mind map nodes
 */
export interface NodeFilters {
  selectedDepths: Set<string>; // e.g., Set(['L1', 'L2'])
  selectedRoots: Set<string>; // e.g., Set(['Root.Who.1', 'Root.What.1'])
  hideNodesWithoutDependencies: boolean;
  showGhostNodes: boolean; // Show dependency targets that are filtered out or don't exist
}

/**
 * Extract all unique depth levels from QA pairs
 */
export function extractDepthLevels(qaPairs: QAPair[]): string[] {
  const depths = new Set<string>();
  for (const pair of qaPairs) {
    depths.add(pair.question.depth);
  }
  return Array.from(depths).sort();
}

/**
 * Extract all root nodes (L1 nodes that are direct children of SUCCESS_CRITERIA)
 */
export function extractRootNodes(qaPairs: QAPair[]): QAPair[] {
  return qaPairs.filter(
    pair => pair.question.depth === 'L1' && pair.question.id.startsWith('Root.')
  );
}

/**
 * Find the root ancestor of a node by traversing up the tree
 */
export function findRootAncestor(
  nodeId: string,
  qaPairs: QAPair[]
): string | null {
  // Create a map for quick lookup
  const nodeMap = new Map(qaPairs.map(pair => [pair.question.id, pair]));

  let currentId = nodeId;
  let current = nodeMap.get(currentId);

  // Traverse up until we find a root node or run out of parents
  while (current) {
    // If this is a root node (L1 starting with Root.), return it
    if (current.question.depth === 'L1' && current.question.id.startsWith('Root.')) {
      return current.question.id;
    }

    // Move to parent
    if (current.parentId) {
      currentId = current.parentId;
      current = nodeMap.get(currentId);
    } else {
      break;
    }
  }

  return null;
}

/**
 * Check if a node should be visible based on current filters
 */
export function isNodeVisible(
  pair: QAPair,
  filters: NodeFilters,
  qaPairs: QAPair[]
): boolean {
  // Always show SUCCESS_CRITERIA node regardless of filters
  if (pair.question.id === 'SUCCESS_CRITERIA') {
    return true;
  }

  // Depth filter - hide nodes whose depth is not selected
  if (!filters.selectedDepths.has(pair.question.depth)) {
    return false;
  }

  // Root filter - check if node belongs to a root branch
  const rootAncestor = findRootAncestor(pair.question.id, qaPairs);
  if (rootAncestor) {
    // This node is part of a root branch - only show if its root is selected
    if (!filters.selectedRoots.has(rootAncestor)) {
      return false;
    }
  }

  // Dependency filter
  if (filters.hideNodesWithoutDependencies) {
    // A node is visible if it has dependencies
    if (pair.answer.dependencies.length === 0) {
      return false;
    }
  }

  return true;
}

/**
 * Filter QA pairs based on current filters
 */
export function applyFilters(
  qaPairs: QAPair[],
  filters: NodeFilters
): QAPair[] {
  return qaPairs.filter(pair => isNodeVisible(pair, filters, qaPairs));
}

/**
 * Get display label for a root node
 */
export function getRootNodeLabel(rootPair: QAPair): string {
  // Extract the type from Root.Type.# format
  const parts = rootPair.question.id.split('.');
  if (parts.length >= 2 && parts[0] === 'Root') {
    const type = parts[1];
    const number = parts[2] || '1';
    return `${type}.${number}: ${rootPair.question.breadcrumb}`;
  }
  return rootPair.question.breadcrumb || rootPair.question.id;
}

/**
 * Create default filters (all enabled)
 */
export function createDefaultFilters(qaPairs: QAPair[]): NodeFilters {
  const depths = extractDepthLevels(qaPairs);
  const roots = extractRootNodes(qaPairs);

  return {
    selectedDepths: new Set(depths),
    selectedRoots: new Set(roots.map(r => r.question.id)),
    hideNodesWithoutDependencies: false,
    showGhostNodes: false,
  };
}

/**
 * Statistics about filtered results
 */
export interface FilterStats {
  totalNodes: number;
  visibleNodes: number;
  hiddenNodes: number;
  percentageVisible: number;
}

/**
 * Calculate filter statistics
 */
export function calculateFilterStats(
  allPairs: QAPair[],
  filteredPairs: QAPair[]
): FilterStats {
  const totalNodes = allPairs.length;
  const visibleNodes = filteredPairs.length;
  const hiddenNodes = totalNodes - visibleNodes;
  const percentageVisible = totalNodes > 0 ? (visibleNodes / totalNodes) * 100 : 100;

  return {
    totalNodes,
    visibleNodes,
    hiddenNodes,
    percentageVisible,
  };
}
